import React, {ComponentProps, ReactNode, useMemo, useState} from 'react';
import {Text} from '@packages/systems/spring';
import {useSpring, animated} from '@react-spring/web';
import type {CSSProperties} from 'react';

interface ShimmerTextProps extends ComponentProps<typeof Text> {
  isActive: boolean;
  children: ReactNode;
  variant?: 'default' | 'path';
  colorVariant?: ComponentProps<typeof Text>['colorVariant'];
}

const AnimatedText = animated(Text);
const AnimatedSpan = animated.span;

// Dynamic shimmer animation that adjusts to the length of the text
export const ShimmerText = ({
  style,
  isActive,
  children,
  variant = 'default',
  colorVariant = 'secondary',
}: ShimmerTextProps) => {
  const [isHovered, setIsHovered] = useState(false);

  // animate the shimmer position
  const positionSpring = useSpring({
    from: {x: 100},
    to: async (next) => {
      // The while loop creates a continuous animation cycle
      // isActive is a prop that can change externally - when it becomes false,
      // the animation loop will stop automatically
      // eslint-disable-next-line no-unmodified-loop-condition
      while (isActive) {
        // 'next' is a function provided by React Spring that allows us to
        // sequence multiple animation steps. Each call to 'next' defines
        // the next animation state to transition to
        // Step 1: Animate shimmer from right (100%) to left (-100%)
        await next({x: -100});
        // Step 2: Hold the shimmer off-screen for 400ms (pause between loops)
        await next({x: -100, config: {duration: 400}});
        // Step 3: Instantly reset position to start the next loop
        // 'immediate: true' makes this transition happen without animation
        await next({x: 100, immediate: true});
      }
    },
    config: {duration: 2000},
    pause: !isActive,
  });

  // Animate opacity for smooth fade between normal and shimmer text
  const fadeSpring = useSpring({
    shimmerOpacity: isActive ? 1 : 0,
    normalOpacity: isActive ? 0 : 1,
    config: {duration: 250},
  });

  // Show/hide underline for path variant
  const showUnderline = variant === 'path' && isHovered;

  const shimmerStyles = useMemo(() => {
    return {
      // Declare background properties first
      background:
        'linear-gradient(90deg, #888, #888 40%, #fff 50%, #888 60%, #888)',
      backgroundSize: '200% 100%',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      backgroundPosition: positionSpring.x.to((x) => `${x}% 0%`),
      // Then apply other styles
      ...style,
      position: 'absolute' as const,
      left: 0,
      top: 0,
      color: 'transparent',
      opacity: fadeSpring.shimmerOpacity,
    };
  }, [style, positionSpring.x, fadeSpring.shimmerOpacity]);

  const normalStyles = useMemo(() => {
    return {
      ...style,
      top: 0,
      left: 0,
      position: 'absolute' as const,
      opacity: fadeSpring.normalOpacity,
    };
  }, [style, fadeSpring.normalOpacity]);

  const containerStyles = useMemo(() => {
    return {
      position: 'relative' as const,
      display: 'inline-block' as const,
    };
  }, []);

  // Invisible text to maintain proper container dimensions
  const spacerStyles = useMemo<CSSProperties>(
    () => ({
      ...style,
      visibility: 'hidden',
      pointerEvents: 'none',
    }),
    [style]
  );

  const underlineStyles = useMemo(() => {
    if (variant !== 'path' || !showUnderline) return {display: 'none' as const};

    return {
      position: 'absolute' as const,
      bottom: 0,
      left: 0,
      height: '1px',
      backgroundColor: 'currentColor',
      width: '100%',
      opacity: 0.4,
    };
  }, [variant, showUnderline]);

  const handleMouseEnter = () => {
    if (variant === 'path') {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (variant === 'path') {
      setIsHovered(false);
    }
  };

  return (
    <AnimatedSpan
      style={containerStyles}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Spacer text to maintain container dimensions */}
      <Text colorVariant={colorVariant} style={spacerStyles}>
        {children}
      </Text>
      {/* Normal text (fades out when shimmer is active) */}
      <AnimatedText colorVariant={colorVariant} style={normalStyles}>
        {children}
      </AnimatedText>
      {/* Shimmer text (fades in when active, overlaid on top) */}
      <AnimatedText colorVariant={colorVariant} style={shimmerStyles}>
        {children}
      </AnimatedText>
      {/* Underline for path variant */}
      {variant === 'path' && <div style={underlineStyles} />}
    </AnimatedSpan>
  );
};

ShimmerText.displayName = 'ShimmerText';
